import type { RouteRecordRaw } from 'vue-router'

import {
  ReviewsAgenciesList,
  ReviewsAgencyDataProvider,
  ReviewsAgencyDetails,
  ReviewsAgencyDetailsSubheader,
  ReviewsAgencyIndustriesAndServices,
  ReviewsAgencyIndustriesAndServicesSubheader,
  ReviewsAgencyIndustriesAndServicesEdit,
  ReviewsAgencyTraits,
  ReviewsAgencyTraitsSubheader,
  ReviewsAgencyView,
  ReviewsAgencyPartnersSubheader,
  ReviewsAgencyPartners,
  ReviewsAgencyAssets,
  ReviewsAgencyResources,
  ReviewsAgencyResourcesWidgets,
  ReviewsAgencyResourcesBadges,
  ReviewsAgencyReviews,
  ReviewsAgencyReviewContainer,
  ReviewsAgencyEdit,
  ReviewsAgencyAssetsEdit,
  ReviewsAgencyAssetsSubheader,
  ReviewsAgencyDetailsEdit,
  ReviewsAgencyTraitsEdit,
  ReviewsAgencyPartnersEdit,
  ReviewsAgencyPreferences,
  ReviewsAgencyPreferencesEdit,
  ReviewsAgencyLeads,
  ReviewsAgencyLeadDetail,
} from '@/components/ReviewsAgenciesManagements'
import { FEATURE_FLAG } from '@/composables/featureFlagCatalog'
import { ReviewsAgencyRouteTabs, ReviewsAgencySubPageRoute, ReviewsRouteName } from '@/router/types'
import { ReviewsAgenciesDataProvider } from '@/views'

export const reviewsAgenciesRoutes: RouteRecordRaw = {
  path: '/agencies',
  component: ReviewsAgenciesDataProvider,
  children: [
    {
      path: '',
      name: ReviewsRouteName.ReviewsAgencies,
      redirect: { name: ReviewsRouteName.ReviewsAgenciesIndex },
    },
    {
      path: 'index',
      name: ReviewsRouteName.ReviewsAgenciesIndex,
      props: true,
      component: ReviewsAgenciesList,
    },
    {
      path: ':agencyId',
      props: true,
      component: ReviewsAgencyDataProvider,
      children: [
        {
          path: 'resources',
          name: ReviewsRouteName.ReviewsAgencyResourcesView,
          props: true,
          component: ReviewsAgencyResources,
          meta: {
            tabId: ReviewsAgencySubPageRoute.Widgets,
          },
          redirect: { name: ReviewsRouteName.ReviewsAgencyResourcesWidgetsView },
          children: [
            {
              path: ReviewsAgencySubPageRoute.Widgets,
              name: ReviewsRouteName.ReviewsAgencyResourcesWidgetsView,
              props: true,
              component: ReviewsAgencyResourcesWidgets,
              meta: {
                tabId: ReviewsAgencySubPageRoute.Widgets,
              },
            },
            {
              path: ReviewsAgencySubPageRoute.Badges,
              name: ReviewsRouteName.ReviewsAgencyResourcesBadgesView,
              props: true,
              component: ReviewsAgencyResourcesBadges,
              meta: {
                tabId: ReviewsAgencySubPageRoute.Badges,
              },
            },
          ],
        },
        {
          path: 'reviews',
          name: ReviewsRouteName.ReviewsAgencyReviewsView,
          props: true,
          component: ReviewsAgencyReviews,
          meta: { featureFlag: FEATURE_FLAG.REVIEWS_AGENCIES.REVIEWS_RELEASE },
        },
        {
          path: 'reviews/:reviewId',
          name: ReviewsRouteName.ReviewsAgencyReviewDetails,
          props: true,
          component: ReviewsAgencyReviewContainer,
          meta: { featureFlag: FEATURE_FLAG.REVIEWS_AGENCIES.REVIEWS_RELEASE },
        },
        {
          path: 'leads',
          name: ReviewsRouteName.ReviewsAgencyLeadsView,
          props: true,
          component: ReviewsAgencyLeads,
          meta: { featureFlag: FEATURE_FLAG.REVIEWS_AGENCIES.LEADS_RELEASE },
        },
        {
          path: 'leads/:leadId',
          name: ReviewsRouteName.ReviewsAgencyLeadDetail,
          props: true,
          component: ReviewsAgencyLeadDetail,
          meta: { featureFlag: FEATURE_FLAG.REVIEWS_AGENCIES.LEADS_RELEASE },
        },
        {
          path: 'preferences',
          name: ReviewsRouteName.ReviewsAgencyPreferencesView,
          props: true,
          component: ReviewsAgencyPreferences,
        },
        {
          path: 'preferences/edit',
          name: ReviewsRouteName.ReviewsAgencyPreferencesEdit,
          props: true,
          component: ReviewsAgencyPreferencesEdit,
        },
        {
          path: '',
          name: ReviewsRouteName.ReviewsAgencyView,
          redirect: { name: ReviewsRouteName.ReviewsAgencyViewDetails },
          props: true,
          component: ReviewsAgencyView,
          children: [
            {
              path: ReviewsAgencyRouteTabs.Details,
              name: ReviewsRouteName.ReviewsAgencyViewDetails,
              props: true,
              components: { default: ReviewsAgencyDetails, subheader: ReviewsAgencyDetailsSubheader },
              meta: {
                tabId: ReviewsAgencyRouteTabs.Details,
              },
            },
            {
              path: ReviewsAgencyRouteTabs.IndustriesAndServices,
              name: ReviewsRouteName.ReviewsAgencyViewIndustriesAndServices,
              props: true,
              components: { default: ReviewsAgencyIndustriesAndServices, subheader: ReviewsAgencyIndustriesAndServicesSubheader },
              meta: {
                tabId: ReviewsAgencyRouteTabs.IndustriesAndServices,
              },
            },
            {
              path: ReviewsAgencyRouteTabs.Traits,
              name: ReviewsRouteName.ReviewsAgencyViewTraits,
              props: true,
              components: { default: ReviewsAgencyTraits, subheader: ReviewsAgencyTraitsSubheader },
              meta: {
                tabId: ReviewsAgencyRouteTabs.Traits,
              },
            },
            {
              path: ReviewsAgencyRouteTabs.Partners,
              name: ReviewsRouteName.ReviewsAgencyViewPartners,
              props: true,
              components: { default: ReviewsAgencyPartners, subheader: ReviewsAgencyPartnersSubheader },
              meta: {
                tabId: ReviewsAgencyRouteTabs.Partners,
              },
            },
            {
              path: ReviewsAgencyRouteTabs.Assets,
              name: ReviewsRouteName.ReviewsAgencyViewAssets,
              props: true,
              components: { default: ReviewsAgencyAssets, subheader: ReviewsAgencyAssetsSubheader },
              meta: {
                tabId: ReviewsAgencyRouteTabs.Assets,
              },
            },
          ],
        },
        {
          path: 'edit',
          redirect: { name: ReviewsRouteName.ReviewsAgencyEditDetails },
          name: ReviewsRouteName.ReviewsAgencyEdit,
          props: true,
          component: ReviewsAgencyEdit,
          children: [
            {
              path: ReviewsAgencyRouteTabs.Partners,
              name: ReviewsRouteName.ReviewsAgencyEditPartners,
              props: true,
              component: ReviewsAgencyPartnersEdit,
              meta: {
                tabId: ReviewsAgencyRouteTabs.Partners,
              },
            },
            {
              path: ReviewsAgencyRouteTabs.Assets,
              name: ReviewsRouteName.ReviewsAgencyEditAssets,
              props: true,
              component: ReviewsAgencyAssetsEdit,
              meta: {
                tabId: ReviewsAgencyRouteTabs.Assets,
              },
            },
            {
              path: ReviewsAgencyRouteTabs.IndustriesAndServices,
              name: ReviewsRouteName.ReviewsAgencyEditIndustriesAndServices,
              props: true,
              component: ReviewsAgencyIndustriesAndServicesEdit,
              meta: {
                tabId: ReviewsAgencyRouteTabs.IndustriesAndServices,
              },
            },
            {
              path: ReviewsAgencyRouteTabs.Details,
              name: ReviewsRouteName.ReviewsAgencyEditDetails,
              props: true,
              component: ReviewsAgencyDetailsEdit,
              meta: {
                tabId: ReviewsAgencyRouteTabs.Details,
              },
            },
            {
              path: ReviewsAgencyRouteTabs.Traits,
              name: ReviewsRouteName.ReviewsAgencyEditTraits,
              props: true,
              component: ReviewsAgencyTraitsEdit,
              meta: {
                tabId: ReviewsAgencyRouteTabs.Traits,
              },
            },
          ],
        },
      ],
    },
  ],
}
